import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { useModalConfirm } from "@ryde/redux";
import {
  ConfirmResult,
  currencySignSelector,
  dropdownAtomFamily,
  formatPrice,
  NO_VALUE_DISPLAY,
} from "@ryde/shared";

import { ColorIconButton } from "../../../controls";
import { IAreaAdditionItemForm } from "./area-addition.models";
import { AreaAdditionDisplayItemStyled } from "./area-addition.styled";

export const AreaAdditionItemDisplay: FC<IAreaAdditionItemForm> = ({
  removeHandler,
  index,
  onEdit,
  value,
}) => {
  const { t } = useTranslation();
  const { confirmDialogResult } = useModalConfirm();
  const areaItems = useRecoilValue(dropdownAtomFamily("area"));
  const currencySign = useRecoilValue(currencySignSelector);

  const onArchiveClickEvent = useEvent(async (): Promise<void> => {
    const confirmationResult = await confirmDialogResult({
      title: t("general.CONFIRM"),
      message: t("general.ITEM_DELETE_MESSAGE"),
    });

    if (confirmationResult === ConfirmResult.OK) {
      removeHandler?.(index || 0);
    }
  });

  const areaItemNames: string = useMemo(() => {
    if (!value?.areaIds?.length) {
      return NO_VALUE_DISPLAY;
    }

    const items =
      value?.areaIds?.map(
        areaId =>
          areaItems.find(item => +item.value === +areaId)?.label ||
          NO_VALUE_DISPLAY,
      ) || [];
    return items?.join(",");
  }, [areaItems, value?.areaIds]);

  const startingFromAreaIndex = useMemo(() => {
    if (!value?.startingFromAreaIndex) {
      return NO_VALUE_DISPLAY;
    }

    return t("priceRate.SPECIFIC_PRICE_FROM_AREA", {
      fromAreaIndex: value?.startingFromAreaIndex,
    });
  }, [value?.startingFromAreaIndex, t]);

  return (
    <AreaAdditionDisplayItemStyled>
      {/* column order mirrors the station-additions display: index → price → areas */}
      <p>{startingFromAreaIndex}</p>
      <p>
        {currencySign} {formatPrice({ value: value?.price || 0 })}
      </p>
      <p className="break-word">{areaItemNames}</p>
      <div>
        <ColorIconButton iconType="EDIT" onClick={() => onEdit?.()} />
        <ColorIconButton
          iconType="DELETE"
          onClick={() => onArchiveClickEvent()}
        />
      </div>
    </AreaAdditionDisplayItemStyled>
  );
};
